import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { FaHistory, FaInfoCircle, FaMagic, FaRegStar, FaStar } from "react-icons/fa";
import Graph from "../components/Graph";
import CodexEvaluationReport from "../components/CodexEvaluationReport";
import EventInfo from "../components/EventInfo";
import ChartEvaluationPopup from "../components/popups/ChartEvaluationPopup";
import TradePopup from "../components/popups/TradePopup";
import TradingPanel from "../trades/TradingPanel";
import AssessmentTile from "./AssessmentTile";
import { buildChartEvaluationTradePrefill, selectExecutableEvaluationPlan } from "../chartEvaluation/chartEvaluationTrade";
import { getAiAssessment } from "../utils/utils";
import { processCommand } from "../components/subscribes/tv_charts/src/helpers";
import {
  assessmentSelection, assessmentStarKey, assessmentStarredSnapshot, coinDataFor,
} from "./assessmentView";

const label = (value) => String(value || "").replaceAll("_", " ");
const title = (value) => label(value).toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

function number(value) {
  return Number.isFinite(Number(value))
    ? Number(value).toLocaleString("en-US", { maximumFractionDigits: 8 }) : "Not available";
}

function when(value) {
  return Number.isFinite(Number(value)) ? new Date(Number(value)).toLocaleString() : "Not available";
}

function marketValue(value) {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  return String(value);
}

export default function AssessmentChartView({
  items, selectedId, onSelect, onLoadMore, hasMore, onClose, authenticated, insights, settings, setSettings,
  onAccessError,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartFailed, setChartFailed] = useState(false);
  const [tradingPanelOpen, setTradingPanelOpen] = useState(false);
  const [assessmentDrawerOpen, setAssessmentDrawerOpen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showChartEvaluation, setShowChartEvaluation] = useState(false);
  const [chartTimeframe, setChartTimeframe] = useState(null);
  const [tradeWindowOpen, setTradeWindowOpen] = useState(false);
  const [tradePopupMode, setTradePopupMode] = useState(null);
  const [tradePopupData, setTradePopupData] = useState({});
  const [tradePopupDisabled, setTradePopupDisabled] = useState(false);
  const pendingNext = useRef(null);
  const loadingMore = useRef(false);
  const reportRef = useRef(null);
  const railListRef = useRef(null);
  const [compactMobile, setCompactMobile] = useState(() => typeof window !== "undefined"
    && window.matchMedia("(max-width: 760px)").matches);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setCompactMobile(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!selectedId || !authenticated) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setDetail(null);
    setError(null);
    setChartFailed(false);
    setTradingPanelOpen(false);
    setAssessmentDrawerOpen(false);
    setShowInfoModal(false);
    setShowChartEvaluation(false);
    setChartTimeframe(null);
    setTradeWindowOpen(false);
    getAiAssessment(selectedId, { signal: controller.signal }).then((value) => {
      if (!controller.signal.aborted) setDetail(value);
    }).catch((requestError) => {
      if (controller.signal.aborted) return;
      setDetail(null);
      setError(requestError);
      if ([401, 403, 503].includes(requestError?.status)) onAccessError?.(requestError);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [authenticated, onAccessError, selectedId]);

  useEffect(() => {
    const pending = pendingNext.current;
    if (!pending || items.length <= pending.previousLength) return;
    pendingNext.current = null;
    loadingMore.current = false;
    const index = items.findIndex((item) => item.id === pending.fromId);
    if (index >= 0 && items[index + 1]) onSelect(items[index + 1].id);
  }, [items, onSelect]);

  useEffect(() => {
    if (compactMobile || !selectedId) return;
    const escapedId = window.CSS?.escape ? window.CSS.escape(selectedId) : selectedId;
    railListRef.current?.querySelector(`[data-assessment-id="${escapedId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [compactMobile, selectedId]);

  useEffect(() => {
    const openTradePopup = (event) => {
      if (!authenticated) {
        alert("Please sign in with Telegram to use the trade window.");
        return;
      }
      const eventData = event?.detail || {};
      if (event.type === "trade_window") {
        setTradePopupMode(localStorage.getItem("tradePopup") || "PRICE");
        setTradePopupData(eventData);
      } else {
        const tradedCurrencies = JSON.parse(localStorage.getItem("tradedCurrencies") || "{}");
        setTradePopupMode("TPSL");
        setTradePopupData({
          ...eventData,
          tradeSide: tradedCurrencies[eventData.currency]?.tradeSide || eventData.tradeSide || "LONG",
        });
      }
      setTradeWindowOpen(true);
    };
    window.addEventListener("trade_window", openTradePopup);
    window.addEventListener("tpsl", openTradePopup);
    return () => {
      window.removeEventListener("trade_window", openTradePopup);
      window.removeEventListener("tpsl", openTradePopup);
    };
  }, [authenticated]);

  const selectedIndex = items.findIndex((item) => item.id === selectedId);
  const selection = useMemo(() => assessmentSelection(detail), [detail]);
  const evaluation = selection?.sectionEvents?.[0]?.codexEvaluation || null;
  const coinData = detail ? coinDataFor(insights, detail.symbol) : null;
  const outsideFilter = detail && selectedIndex < 0;
  const eventTimeframe = detail?.event?.timeframe || detail?.eventSnapshot?.tf?.replace(/^_/, "") || null;
  const eventPrice = detail?.event?.price ?? detail?.assessedPrice;
  const currentPrice = coinData?.price;
  const sentiment = detail?.sentimentContext;
  const tradePlan = useMemo(() => selectExecutableEvaluationPlan({
    evaluation,
    requestedSide: detail?.side,
  }), [detail?.side, evaluation]);
  const selectedEvent = selection?.sectionEvents?.[0] || null;
  const starredKey = assessmentStarKey(detail?.id);
  const starSelected = Boolean(starredKey && settings?.starred?.[starredKey]);
  const starDisabled = !starredKey || !selectedEvent || typeof setSettings !== "function";

  const closeWorkspace = () => {
    setAssessmentDrawerOpen(false);
    setShowInfoModal(false);
    setShowChartEvaluation(false);
    setTradingPanelOpen(false);
    onClose();
  };

  const toggleStar = () => {
    if (starDisabled) return;
    const starred = { ...(settings?.starred || {}) };
    if (starSelected) delete starred[starredKey];
    else {
      const snapshot = assessmentStarredSnapshot(detail, selection);
      if (!snapshot) return;
      starred[starredKey] = snapshot;
    }
    setSettings({ ...(settings || {}), starred });
  };

  const drawEvaluationLevels = (levels) => {
    const symbol = selection?.chartMarket?.symbol;
    if (!symbol) return;
    window.dispatchEvent(new CustomEvent("chart_evaluation_draw", {
      detail: { symbol, levels },
    }));
  };

  const takeEvaluationTrade = (evaluationResult) => {
    const symbol = selection?.chartMarket?.symbol;
    if (!symbol || (evaluationResult?.symbol && evaluationResult.symbol !== symbol)) return;
    if (!selectExecutableEvaluationPlan(evaluationResult)) return;
    setTradePopupMode("PRICE");
    setTradePopupData({
      currency: selectedEvent?.cur,
      candle: chartTimeframe || eventTimeframe || selectedEvent?.tf,
      ...buildChartEvaluationTradePrefill(evaluationResult),
    });
    setShowChartEvaluation(false);
    setTradeWindowOpen(true);
  };

  const actionButtons = <div className="assessmentActionButtons" aria-label="Assessment actions">
    <button type="button" className="assessmentActionButton assessmentActionMagic"
      disabled={!selection?.chartMarket?.symbol} onClick={() => setShowChartEvaluation(true)}
      aria-label="Evaluate chart"><FaMagic /></button>
    <button type="button" className="assessmentActionButton assessmentActionInfo"
      disabled={!selectedEvent} onClick={() => setShowInfoModal(true)}
      aria-label="Event info"><FaInfoCircle /></button>
    <button type="button"
      className={`assessmentActionButton assessmentActionStar${starSelected ? " assessmentActionStarSelected" : ""}`}
      disabled={starDisabled} onClick={toggleStar}
      aria-label={starSelected ? "Remove assessment from Starred" : "Add assessment to Starred"}
      aria-pressed={starSelected}>
      {starSelected ? <FaStar /> : <FaRegStar />}
    </button>
  </div>;

  const essentialIdentity = detail && <div className="assessmentIdentity">
    <strong>{detail.symbol}</strong>
    <span className={`assessmentSide assessmentSide${title(detail.side)}`}>{detail.side}</span>
    <span>{title(detail.type)}</span>
    <span>{title(detail.provider)}</span>
    <span>{title(detail.trigger)}</span>
    <span>{title(detail.outcome)}</span>
    {detail.sentimentImpact && <span>Global {title(detail.sentimentImpact)}</span>}
  </div>;

  const essentialMetrics = detail && <div className="assessmentEssentialGrid">
    <div><span>Adjusted confidence</span><strong>{Number.isFinite(detail.confidence) ? `${detail.confidence}%` : "Not available"}</strong></div>
    <div><span>Technical confidence</span><strong>{Number.isFinite(detail.technicalConfidence) ? `${detail.technicalConfidence}%` : "Not available"}</strong></div>
    <div><span>Score</span><strong>{Number.isFinite(detail.score) ? detail.score : "Not available"}</strong></div>
    <div><span>Timing</span><strong>{detail.timing ? title(detail.timing) : "Not available"}</strong></div>
    <div><span>Event price</span><strong>{number(eventPrice)}</strong></div>
    <div><span>Current price</span><strong>{number(currentPrice)}</strong></div>
    <div><span>24h move</span><strong>{Number.isFinite(Number(coinData?._24H)) ? `${Number(coinData._24H).toFixed(2)}%` : "Not available"}</strong></div>
    <div><span>Market cap</span><strong>{marketValue(coinData?.mcap)}</strong></div>
  </div>;

  const selectFromRail = (id) => {
    setAssessmentDrawerOpen(false);
    onSelect(id);
  };

  const move = async (step) => {
    if (!items.length) return;
    if (selectedIndex < 0) {
      onSelect(items[step > 0 ? 0 : items.length - 1].id);
      return;
    }
    const index = selectedIndex;
    const candidate = items[index + step];
    if (candidate) {
      onSelect(candidate.id);
      return;
    }
    if (step > 0 && hasMore) {
      if (loadingMore.current) return;
      loadingMore.current = true;
      pendingNext.current = { fromId: selectedId, previousLength: items.length };
      const loaded = await onLoadMore();
      loadingMore.current = false;
      if (!loaded) pendingNext.current = null;
      return;
    }
    onSelect(items[step > 0 ? 0 : items.length - 1].id);
  };

  return (
    <section className="assessmentChartView" aria-label="Assessment chart view">
      <div className="assessmentWorkspace">
        <aside className="assessmentRail" aria-label={tradingPanelOpen ? "Trading panel" : "Assessment list"}>
          <div className="assessmentRailBack">
            <button type="button" className="historyBtn" onClick={closeWorkspace}>← Assessments</button>
          </div>
          {tradingPanelOpen && selection ? <TradingPanel
            currency={selection.sectionEvents[0].cur}
            timeframe={eventTimeframe}
            selectedEvent={selection.sectionEvents[0]}
            open={tradingPanelOpen}
            onClose={() => setTradingPanelOpen(false)}
            initialSide={tradePlan?.side || detail?.side}
            initialEntryPrice={tradePlan?.entry}
            initialStopPrice={tradePlan?.stopLoss}
            initialTakePrice={tradePlan?.takeProfit1}
          /> : <>
            <div className="assessmentRailHeader">
              <div>
                <strong>Assessments</strong>
                <span>{items.length}{hasMore ? "+" : ""} available</span>
              </div>
              <button type="button" className="assessmentTradeButton" disabled={!selection}
                onClick={() => setTradingPanelOpen(true)}>Trade</button>
            </div>
            <div className="assessmentRailList" ref={railListRef}>
              {items.map((assessment) => <div key={assessment.id}
                className={`assessmentRailItem${assessment.id === selectedId ? " assessmentRailItemSelected" : ""}`}>
                <AssessmentTile compact assessment={assessment} coinData={coinDataFor(insights, assessment.symbol)} onOpen={selectFromRail} />
              </div>)}
              {hasMore && <button type="button" className="historyBtn assessmentRailLoadMore"
                onClick={onLoadMore}>Load more</button>}
            </div>
          </>}
        </aside>

        <main className="assessmentMain">
          <header className="assessmentChartHeader">
            <div className="assessmentChartHeading">
              <span className="assessmentEssentialEyebrow">AI assessment history</span>
              <h2>{detail?.event?.name || "Recorded assessment"}</h2>
            </div>
            <div className="assessmentChartHeaderControls">
              <div className="assessmentChartNavigation">
                <button type="button" className="historyBtn assessmentArrow" aria-label="Previous assessment" onClick={() => move(-1)}>←</button>
                <span>{selectedIndex >= 0 ? `${selectedIndex + 1} / ${items.length}${hasMore ? "+" : ""}` : "Direct link"}</span>
                <button type="button" className="historyBtn assessmentArrow" aria-label="Next assessment" onClick={() => move(1)}>→</button>
              </div>
              {actionButtons}
            </div>
          </header>

          <div className="assessmentChartContent">
            {loading && <div className="assessmentState">Loading recorded assessment…</div>}
            {!loading && error && <div className="assessmentState assessmentError">
              {error.status === 404
                ? "This assessment is unavailable or outside retained history."
                : "The assessment could not be loaded."}
            </div>}
            {outsideFilter && <div className="assessmentOutsideFilter">This assessment is outside the active filter. Its authorized saved detail is shown directly.</div>}
            {!loading && selection && <>
              <header className="assessmentMobileChartHeader">
                <div className="assessmentMobileTitleRow">
                  <div>
                    <h2>{detail.event?.name || "Recorded assessment"}</h2>
                    <strong>CAP: {marketValue(coinData?.mcap)}</strong>
                  </div>
                  {actionButtons}
                </div>
                <div className="assessmentMobileNavigation">
                  <button type="button" aria-label="Previous assessment" onClick={() => move(-1)}>←</button>
                  <span>{selectedIndex >= 0 ? `${selectedIndex + 1} / ${items.length}${hasMore ? "+" : ""}` : "Direct link"}</span>
                  <button type="button" aria-label="Next assessment" onClick={() => move(1)}>→</button>
                </div>
              </header>
              {selection.contextAvailability !== "RECORDED" && <div className="assessmentOutsideFilter">
                Some legacy event evidence was not recorded, so only the available saved context is shown.
              </div>}
              {selection.chartAvailable ? (
                <div className="assessmentChartStage">
                  <Graph
                    selectedEventData={selection}
                    assessmentContext={{
                      id: detail.id,
                      evaluation,
                      snapshot: selection.sectionEvents[0],
                      focusTime: selection.focusTime,
                      provider: detail.provider,
                    }}
                    btcRegime={insights?.btcRegime}
                    compactMobile={compactMobile}
                    enableTradeCommands
                    onEventChange={() => {}}
                    onTimeframeChange={setChartTimeframe}
                    onTradeEvent={(event) => {
                      const eventType = event?.data?.type;
                      setChartFailed(eventType === "chart_failed");
                      if (eventType === "chart_ready") setTradePopupDisabled(false);
                    }}
                    onCodexEvaluationSelect={() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  />
                </div>
              ) : <div className="assessmentState">Chart unavailable for this recorded market. The assessment report is still available.</div>}
              {chartFailed && <div className="assessmentState assessmentError">Historical candles could not be loaded for this assessment.</div>}
              <section className="assessmentSummary" aria-label="Assessment essentials">
                {essentialIdentity}
                {essentialMetrics}
              </section>
              <TradePopup
                priceBy={tradePopupMode}
                show={tradeWindowOpen}
                draw={selection.sectionEvents[0].draw}
                btnDisabled={tradePopupDisabled}
                setBtnDisabled={setTradePopupDisabled}
                tradeData={tradePopupData}
                chartTimeframe={chartTimeframe}
                onCommand={(command) => {
                  processCommand({ command });
                  setTradeWindowOpen(false);
                }}
                onClose={() => setTradeWindowOpen(false)}
              />
              <section className="assessmentConclusion">
                <h3>{detail.event?.name || selection.sectionEvents[0].name}</h3>
                {selection.sectionEvents[0].desc && <p>{selection.sectionEvents[0].desc}</p>}
                <strong>{evaluation?.summary || evaluation?.eventAlignment || detail.outcome}</strong>
              </section>
              <section className="assessmentRecordedFacts" aria-label="Recorded assessment information">
                <div><span>Event time</span><strong>{when(detail.event?.occurredAt)}</strong></div>
                <div><span>Assessed</span><strong>{when(detail.assessedAt)}</strong></div>
                <div><span>{sentiment?.latest?.horizonDays === 7 ? "Forecast: next 7 days from report time" : "Global 6–24h outlook"}</span><strong>{sentiment?.latest?.verdict || "Not available"}</strong></div>
                {sentiment?.latest?.swing && <div><span>{sentiment.latest.horizonDays === 7 ? 'Current swing' : 'Intraday move'} at assessment</span><strong>{title(sentiment.latest.swing.direction)} · {title(sentiment.latest.swing.state)}</strong></div>}
                {sentiment?.weeklyBackground && <div><span>Daily background · next 7 days</span><strong>{sentiment.weeklyBackground.verdict}</strong></div>}
                <div><span>Model</span><strong>{detail.model || "Not available"}</strong></div>
              </section>
              <section className="assessmentFullReport" ref={reportRef}>
                <h3>Assessment details</h3>
                <CodexEvaluationReport evaluation={evaluation} />
              </section>
            </>}
          </div>
        </main>
      </div>

      {compactMobile && <div className="assessmentMobileActions" aria-label="Assessment actions">
        <button type="button" className="assessmentMobileIconButton" aria-label="Assessments"
          onClick={() => setAssessmentDrawerOpen(true)}><FaHistory /></button>
        <button type="button" className="assessmentMobileTradeButton" disabled={!selection}
          onClick={() => setTradingPanelOpen(true)}>Trade</button>
        <button type="button" className="assessmentMobileIconButton" aria-label="Coin info"
          disabled={!selectedEvent} onClick={() => setShowInfoModal(true)}><FaInfoCircle /></button>
      </div>}

      <footer className="assessmentWorkspaceFooter">
        <Button variant="primary" onClick={closeWorkspace}>Close</Button>
      </footer>

      {compactMobile && assessmentDrawerOpen && <div className="assessmentMobileBackdrop"
        role="presentation" onClick={() => setAssessmentDrawerOpen(false)}>
        <section className="assessmentMobileDrawer" role="dialog" aria-modal="true"
          aria-label="Choose an assessment" onClick={(event) => event.stopPropagation()}>
          <div className="assessmentMobileDrawerHeader">
            <strong>Assessments</strong>
            <button type="button" onClick={() => setAssessmentDrawerOpen(false)}>Close</button>
          </div>
          <div className="assessmentRailList">
            {items.map((assessment) => <div key={assessment.id}
              className={`assessmentRailItem${assessment.id === selectedId ? " assessmentRailItemSelected" : ""}`}>
              <AssessmentTile compact assessment={assessment} coinData={coinDataFor(insights, assessment.symbol)} onOpen={selectFromRail} />
            </div>)}
            {hasMore && <button type="button" className="historyBtn assessmentRailLoadMore"
              onClick={onLoadMore}>Load more</button>}
          </div>
        </section>
      </div>}

      {compactMobile && selection && <TradingPanel
        currency={selection.sectionEvents[0].cur}
        timeframe={eventTimeframe}
        selectedEvent={selection.sectionEvents[0]}
        mobile
        open={tradingPanelOpen}
        onClose={() => setTradingPanelOpen(false)}
        initialSide={tradePlan?.side || detail?.side}
        initialEntryPrice={tradePlan?.entry}
        initialStopPrice={tradePlan?.stopLoss}
        initialTakePrice={tradePlan?.takeProfit1}
      />}

      <ChartEvaluationPopup
        show={showChartEvaluation}
        onHide={() => setShowChartEvaluation(false)}
        symbol={selection?.chartMarket?.symbol || ""}
        eventPrice={selectedEvent?.price}
        originatingEvent={selectedEvent}
        onDrawLevels={drawEvaluationLevels}
        onTakeTrade={takeEvaluationTrade}
      />

      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        className={compactMobile ? "bg-dark info-modal-mobile" : "bg-dark"}
        dialogClassName="custom-modal-width">
        <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-secondary">
          <Modal.Title>Event info</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white overflow-auto" style={{ maxHeight: "80vh" }}>
          {selectedEvent && <EventInfo
            eventInfo={selectedEvent}
            insights={insights}
            settings={settings}
            setSettings={setSettings}
          />}
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary d-flex justify-content-end">
          <Button variant="primary" onClick={() => setShowInfoModal(false)} style={{ fontWeight: 700 }}>Close</Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
